import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { APP_ENVIRONMENT } from "../lib/config";
import { HelpPanel } from "../components/HelpPanel";
import { SiteIcon } from "../components/site/SiteIcon";
import { SiteImage } from "../components/site/ProductCard";
import { PRODUCTS, productsInRange } from "../content/products";
import { BUSINESS, IMAGES, RANGES, quotePath } from "../content/site";

/** Environment badge — hidden in Production on the client portal (per frontend-ui-system.md). */
function EnvBadge() {
  const env = APP_ENVIRONMENT.toLowerCase();
  if (env === "production" || env === "prod") return null;
  const isStaging = env === "staging";
  return (
    <span className="gw-env" data-env={isStaging ? "staging" : "dev"}>
      {isStaging ? "STAGING" : "DEV"}
    </span>
  );
}

const navClass = ({ isActive }: { isActive: boolean }) => (isActive ? "on" : undefined);

/** Drop focus after choosing a mega-menu link so the :focus-within menu closes on navigation. */
function blurActive() {
  const el = document.activeElement;
  if (el instanceof HTMLElement) el.blur();
}

function UtilityBar() {
  return (
    <div className="gw-util">
      <div className="gw-wrap">
        <span>
          <SiteIcon name="pin" size={14} /> {BUSINESS.street}, {BUSINESS.town}
        </span>
        <span className="hide-sm">
          <SiteIcon name="clock" size={14} /> {BUSINESS.hours}
        </span>
        <span className="gw-util-r">
          <span className="hide-md">QBCC licence {BUSINESS.qbcc}</span>
          <a href={BUSINESS.tel} data-loc="utility">
            <SiteIcon name="phone" size={14} /> {BUSINESS.phone}
          </a>
        </span>
      </div>
    </div>
  );
}

function MegaMenu() {
  return (
    <div className="gw-mega">
      {RANGES.map((r) => (
        <div key={r.id}>
          <p className="gw-mega-h">{r.name}</p>
          {productsInRange(r.id).map((p) => (
            <Link key={p.slug} to={`/products/${p.slug}`} onClick={blurActive}>
              {p.name}
              <span>{p.tagline}</span>
            </Link>
          ))}
        </div>
      ))}
      <div className="gw-mega-feat">
        <strong>Not sure where to start?</strong>
        Come in to our display centre for inspiration and friendly free design advice — or let us
        come to you.
        <br />
        <br />
        <Link className="gw-link" to="/contact" onClick={blurActive}>
          Visit us <SiteIcon name="arrow" size={16} />
        </Link>
      </div>
    </div>
  );
}

interface HeaderProps {
  menuOpen: boolean;
  onToggleMenu: () => void;
  onOpenHelp: () => void;
}

function Header({ menuOpen, onToggleMenu, onOpenHelp }: HeaderProps) {
  return (
    <header className="gw-head">
      <div className="gw-wrap gw-head-in">
        <div className="gw-brandwrap">
          <Link className="gw-logo" to="/" aria-label="Greatway Patios — home">
            <SiteImage src={IMAGES.logo} alt="Greatway Patios" eager />
          </Link>
          <EnvBadge />
        </div>
        <nav className="gw-nav" aria-label="Main">
          <div className="gw-dd">
            <NavLink to="/products" className={navClass}>
              Products <SiteIcon name="down" size={16} />
            </NavLink>
            <MegaMenu />
          </div>
          <NavLink to="/our-work" className={navClass}>
            Our work
          </NavLink>
          <NavLink to="/about" className={navClass}>
            About
          </NavLink>
          <NavLink to="/contact" className={navClass}>
            Contact
          </NavLink>
        </nav>
        <div className="gw-head-cta">
          <a className="gw-tel" href={BUSINESS.tel} data-loc="header" aria-label={`Call us ${BUSINESS.phone}`}>
            <span className="gw-tel-ic">
              <SiteIcon name="phone" size={18} />
            </span>
            <span>
              <small>Call us</small>
              {BUSINESS.phone}
            </span>
          </a>
          <button type="button" className="gw-helpbtn" onClick={onOpenHelp} aria-label="Help for this page">
            <span>Help</span>
          </button>
          <Link className="gw-btn gw-btn-red" to={quotePath()}>
            Free quote
          </Link>
          <button
            type="button"
            className="gw-burger"
            aria-label="Menu"
            aria-expanded={menuOpen}
            aria-controls="gw-mob"
            onClick={onToggleMenu}
          >
            <SiteIcon name={menuOpen ? "x" : "menu"} size={26} />
          </button>
        </div>
      </div>
      <nav id="gw-mob" className={`gw-mob${menuOpen ? " open" : ""}`} aria-label="Mobile">
        <Link to="/products">Products</Link>
        {PRODUCTS.map((p) => (
          <Link key={p.slug} className="sub" to={`/products/${p.slug}`}>
            {p.name}
          </Link>
        ))}
        <Link to="/our-work">Our work</Link>
        <Link to="/about">About</Link>
        <Link to="/contact">Contact</Link>
        <a href={BUSINESS.tel}>Call {BUSINESS.phone}</a>
      </nav>
    </header>
  );
}

function Footer() {
  const links = (range: (typeof RANGES)[number]["id"]) =>
    productsInRange(range).map((p) => (
      <Link key={p.slug} to={`/products/${p.slug}`}>
        {p.name}
      </Link>
    ));
  return (
    <footer className="gw-foot">
      <div className="gw-wrap">
        <div className="gw-foot-grid">
          <div>
            <span className="logochip">
              <SiteImage src={IMAGES.logo} alt="Greatway Patios" />
            </span>
            <p>
              Toowoomba’s outdoor living specialists. Locally owned and managed by the Cooper family,
              serving Toowoomba, the Darling Downs and the Lockyer Valley.
            </p>
            <a
              className="gw-social"
              href={BUSINESS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Greatway Patios on Facebook"
            >
              <SiteIcon name="fb" size={18} />
            </a>
          </div>
          <div>
            <h2 className="gw-foot-h">Roofs &amp; patios</h2>
            {links("roofs-and-patios")}
            <h2 className="gw-foot-h" style={{ marginTop: 22 }}>
              Living spaces
            </h2>
            {links("living-spaces")}
          </div>
          <div>
            <h2 className="gw-foot-h">Screens &amp; shade</h2>
            {links("screens-and-shade")}
            <h2 className="gw-foot-h" style={{ marginTop: 22 }}>
              Company
            </h2>
            <Link to="/about">About us</Link>
            <Link to="/our-work">Our work</Link>
            <Link to="/contact">Contact</Link>
          </div>
          <div>
            <h2 className="gw-foot-h">Visit the display centre</h2>
            <div className="row">
              <SiteIcon name="pin" size={16} />
              <span>
                {BUSINESS.street}
                <br />
                {BUSINESS.town}, {BUSINESS.state} {BUSINESS.postcode}
              </span>
            </div>
            <div className="row">
              <SiteIcon name="clock" size={16} />
              <span>{BUSINESS.hours}</span>
            </div>
            <div className="row">
              <SiteIcon name="phone" size={16} />
              <a href={BUSINESS.tel} style={{ padding: 0 }} data-loc="footer">
                {BUSINESS.phone}
              </a>
            </div>
            <div className="row">
              <SiteIcon name="mail" size={16} />
              <a href={`mailto:${BUSINESS.email}`} style={{ padding: 0 }}>
                {BUSINESS.email}
              </a>
            </div>
          </div>
        </div>
        <div className="gw-foot-bot">
          <span>© {new Date().getFullYear()} Greatway Patios. All rights reserved.</span>
          <span>QBCC licence {BUSINESS.qbcc} · Member, Master Builders Queensland</span>
        </div>
      </div>
    </footer>
  );
}

/** Sticky Call / Free quote bar — shown only at container width ≤ 640px (BR-WEB-005, CSS). */
function MobileActionBar() {
  return (
    <div className="gw-mbar" role="region" aria-label="Quick actions">
      <a className="gw-btn gw-btn-line" href={BUSINESS.tel} data-loc="mobile-bar">
        <SiteIcon name="phone" size={18} /> Call
      </a>
      <Link className="gw-btn gw-btn-red" to={quotePath()}>
        Free quote
      </Link>
    </div>
  );
}

/**
 * Shared chrome for every public screen (Feature #25438): utility bar, sticky header with the
 * Products mega-menu, Help (BR-WEB-007), Free quote, the mobile menu, footer and the mobile
 * action bar. The phone renders as a tel: link in the utility bar, header, footer and mobile bar
 * (BR-WEB-003). Breakpoints are container queries on .gw (BR-WEB-005).
 */
export default function PublicLayout() {
  const [helpOpen, setHelpOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname, search } = useLocation();

  // A navigation (not the first load) closes the mobile menu and returns to the top of the new page.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [pathname, search]);

  return (
    <div className="gw">
      <a className="gw-skip" href="#main">
        Skip to content
      </a>
      <UtilityBar />
      <Header
        menuOpen={menuOpen}
        onToggleMenu={() => setMenuOpen((o) => !o)}
        onOpenHelp={() => setHelpOpen(true)}
      />
      <HelpPanel open={helpOpen} onClose={() => setHelpOpen(false)} />
      <main id="main">
        <Outlet />
      </main>
      <Footer />
      <MobileActionBar />
    </div>
  );
}
